<template>
  <div class="sls-log-query">
    <!-- 查询条件区域 - 吸顶 -->
    <div class="query-header" :class="{ 'is-sticky': isSticky }">
      <el-card shadow="never">
        <div class="query-form">
          <!-- 第一行：项目、时间、条数 -->
          <div class="form-row">
            <div class="form-item" style="flex: 2; min-width: 300px;">
              <label class="form-label">日志项目</label>
              <el-select
                v-model="selectedProduct"
                filterable
                placeholder="请选择日志项目"
                @change="handleProductChange"
                style="width: 100%;"
              >
                <el-option
                  v-for="item in productList"
                  :key="item.name"
                  :label="item.name"
                  :value="item.name"
                >
                  <span>{{ item.name }}</span>
                  <span style="float: right; color: #8492a6; font-size: 13px">{{ item.platform }}</span>
                </el-option>
              </el-select>
            </div>
            <div class="form-item" style="flex: 2; min-width: 380px;">
              <label class="form-label">时间范围</label>
              <el-date-picker
                v-model="timeRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                format="YYYY-MM-DD HH:mm:ss"
                value-format="YYYY-MM-DD HH:mm:ss"
                :shortcuts="timeShortcuts"
                @change="handleTimeChange"
                style="width: 100%;"
              />
            </div>
            <div class="form-item" style="flex: 0 0 120px;">
              <label class="form-label">每页条数</label>
              <el-input-number
                v-model="queryParams.lines"
                :min="10"
                :max="100"
                :step="10"
                controls-position="right"
                style="width: 100%;"
              />
            </div>
          </div>

          <!-- 第二行：查询条件、字段选择、操作按钮 -->
          <div class="form-row">
            <div class="form-item" style="flex: 2; min-width: 300px;">
              <label class="form-label">
                查询条件
                <el-tooltip placement="top" effect="light" :width="500">
                  <template #content>
                    <div class="query-help">
                      <div class="help-title">SLS查询语法示例：</div>
                      <div class="help-item">• 精确匹配：{{ messageField }}: "错误信息"</div>
                      <div class="help-item">• 右侧模糊：{{ messageField }}: 关键词*</div>
                      <div class="help-item">• AND条件：{{ messageField }}: "错误" AND level: "ERROR"</div>
                      <div class="help-item">• OR条件：{{ messageField }}: "错误" OR {{ messageField }}: "异常"</div>
                      <div class="help-item">• NOT条件：NOT {{ messageField }}: "DEBUG"</div>
                      <div class="help-item">• 留空查询全部日志</div>
                      <div class="help-title" style="margin-top: 12px;">SQL查询示例：</div>
                      <div class="help-item">• LIKE查询：* | SELECT * WHERE {{ messageField }} LIKE '%错误%'</div>
                      <div class="help-item">• 统计查询：* | SELECT COUNT(*) as cnt GROUP BY level</div>
                      <div class="help-item">• 时间过滤：* | SELECT * WHERE __time__ > 1234567890</div>
                    </div>
                  </template>
                  <el-icon class="help-icon"><QuestionFilled /></el-icon>
                </el-tooltip>
              </label>
              <el-input
                v-model="queryParams.query"
                placeholder="输入查询条件，留空查询全部"
                clearable
                @keyup.enter="handleSearch"
              />
            </div>
            <div class="form-item" style="flex: 1; min-width: 200px;">
              <label class="form-label">显示字段</label>
              <el-select
                v-model="selectedFields"
                multiple
                filterable
                placeholder="选择显示字段"
                @change="handleFieldsChange"
                popper-class="field-select-popper"
                style="width: 100%;"
              >
                <el-option
                  v-for="field in availableFields"
                  :key="field"
                  :label="field"
                  :value="field"
                />
              </el-select>
            </div>
            <div class="form-item" style="flex: 0 0 auto; align-self: flex-end;">
              <div class="form-actions">
                <el-button type="primary" :loading="loading" @click="handleSearch" :icon="Search">
                  查询
                </el-button>
                <el-button type="success" :loading="loading" @click="handleRealtimeQuery" plain>
                  实时查询
                </el-button>
                <el-button @click="handleReset" :icon="RefreshLeft">重置</el-button>
                <el-button @click="handleClear" :icon="Delete">清空</el-button>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 统计信息 -->
    <div class="stats-bar" v-if="logs.length > 0">
      <div class="stats-item">
        <span class="stats-label">总条数：</span>
        <span class="stats-value">{{ total.toLocaleString() }}</span>
      </div>
      <div class="stats-item">
        <span class="stats-label">已加载：</span>
        <span class="stats-value">{{ logs.length.toLocaleString() }}</span>
      </div>
      <div class="stats-item">
        <span class="stats-label">查询模式：</span>
        <span class="stats-value">{{ queryMode }}</span>
      </div>
    </div>

    <!-- 日志内容区域 -->
    <div class="log-content" ref="logContainer" @scroll="handleScroll">
      <div v-if="logs.length === 0 && !loading" class="empty-state">
        <el-empty description="暂无日志数据，请选择项目并查询" />
      </div>

      <div v-else class="log-list">
        <div
          v-for="(log, index) in logs"
          :key="index"
          class="log-line"
          :class="{ 'log-line-odd': index % 2 === 0 }"
          @click="selectLine(index)"
          :data-line="index + 1"
        >
          <span class="log-line-number">{{ index + 1 }}</span>
          <span class="log-actions">
            <el-link 
              v-if="log['__tag__:__pack_id__'] && log['__pack_meta__']"
              type="primary" 
              :underline="false"
              size="small"
              @click.stop="showContext(log)"
            >
              上下文
            </el-link>
            <span v-else style="width: 48px; display: inline-block;"></span>
          </span>
          <span class="log-time">{{ formatTime(log[timeField] || log.__time__ || log.time) }}</span>
          <span class="log-separator">|</span>
          <span 
            class="log-message" 
            @click="showFullContent('日志内容', log[messageField] || log.message || '')"
            @dblclick="copyToClipboard(log[messageField] || log.message || '')"
            :title="'单击查看完整内容，双击复制'"
          >
            {{ log[messageField] || log.message || '' }}
          </span>
          <span v-for="field in getDisplayFields(log)" :key="field" class="log-field">
            <span class="log-separator">|</span>
            <span class="field-label">{{ field }}:</span>
            <span 
              class="field-content" 
              @click="showFullContent(field, log[field])"
              @dblclick="copyToClipboard(formatFieldValue(log[field]))"
              :title="'单击查看完整内容，双击复制'"
            >
              {{ formatFieldValue(log[field]) }}
            </span>
          </span>
        </div>

        <!-- 加载更多提示 -->
        <div v-if="loadingMore" class="loading-more">
          <el-icon class="is-loading"><Loading /></el-icon>
          <span>加载中...</span>
        </div>

        <!-- 没有更多数据提示 -->
        <div v-if="!hasNext && logs.length > 0" class="no-more">
          <el-divider>已加载全部日志</el-divider>
        </div>
      </div>

      <!-- 首次加载 -->
      <div v-if="loading && logs.length === 0" class="loading-state">
        <el-icon class="is-loading" :size="40"><Loading /></el-icon>
        <div>正在查询日志...</div>
      </div>
    </div>

    <!-- 上下文查询对话框 -->
    <el-dialog
      v-model="contextDialogVisible"
      title="日志上下文"
      width="90%"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <div class="context-container">
        <!-- 上下文控制栏 -->
        <div class="context-controls">
          <span class="context-info">
            当前显示 {{ contextLogs.length }} 条日志
          </span>
        </div>

        <!-- 上下文日志列表 -->
        <div class="context-log-list" ref="contextLogContainer" @scroll="handleContextScroll">
          <!-- 顶部：没有更早的日志提示 -->
          <div v-if="!contextData.hasMoreBefore && contextLogs.length > 0" class="context-no-more">
            没有更早的日志了
          </div>

          <!-- 顶部加载提示 -->
          <div v-if="contextLoading && contextData.loadingDirection === 'before'" class="context-loading">
            <el-icon class="is-loading"><Loading /></el-icon>
            <span>加载更早日志...</span>
          </div>

          <div
            v-for="(log, index) in contextLogs"
            :key="index"
            :ref="log.__index_number__ === '0' ? 'currentLogLine' : undefined"
            class="context-log-line"
            :class="{ 
              'context-log-line-odd': index % 2 === 0,
              'context-log-line-current': log.__index_number__ === '0'
            }"
          >
            <span class="context-line-number" :class="{ 'current-line': log.__index_number__ === '0' }">
              {{ getLogLineNumber(index) }}
            </span>
            <span class="log-time">{{ formatTime(log[timeField] || log.__time__ || log.time) }}</span>
            <span class="log-separator">|</span>
            <span 
              class="log-message" 
              @click="showFullContent('日志内容', log[messageField] || log.message || '')"
              @dblclick="copyToClipboard(log[messageField] || log.message || '')"
              :title="'单击查看完整内容，双击复制'"
            >
              {{ log[messageField] || log.message || '' }}
            </span>
            <span v-for="field in getDisplayFields(log)" :key="field" class="log-field">
              <span class="log-separator">|</span>
              <span class="field-label">{{ field }}:</span>
              <span 
                class="field-content" 
                @click="showFullContent(field, log[field])"
                @dblclick="copyToClipboard(formatFieldValue(log[field]))"
                :title="'单击查看完整内容，双击复制'"
              >
                {{ formatFieldValue(log[field]) }}
              </span>
            </span>
          </div>

          <!-- 底部加载提示 -->
          <div v-if="contextLoading && contextData.loadingDirection === 'after'" class="context-loading">
            <el-icon class="is-loading"><Loading /></el-icon>
            <span>加载更晚日志...</span>
          </div>

          <!-- 底部：没有更晚的日志提示 -->
          <div v-if="!contextData.hasMoreAfter && contextLogs.length > 0" class="context-no-more">
            没有更晚的日志了
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 完整内容查看对话框 -->
    <el-dialog
      v-model="fullContentDialogVisible"
      :title="fullContentData.title"
      width="60%"
      :close-on-click-modal="false"
    >
      <div class="full-content-container">
        {{ fullContentData.content }}
      </div>
      <template #footer>
        <el-button @click="fullContentDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="copyToClipboard(fullContentData.content)">复制</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, RefreshLeft, Delete, QuestionFilled, Loading } from '@element-plus/icons-vue'
import http from '@/utils/http'

// 数据
const productList = ref([])
const selectedProduct = ref('')
const currentProduct = ref(null)
const timeRange = ref([])
const selectedFields = ref([])
const availableFields = ref([])
const logs = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const total = ref(0)
const hasNext = ref(false)
const queryMode = ref('simple')
const isSticky = ref(false)
const logContainer = ref(null)

// 上下文查询相关
const contextDialogVisible = ref(false)
const contextLoading = ref(false)
const contextLogs = ref([])
const contextLogContainer = ref(null)
const currentLogLine = ref(null)
const contextInitialized = ref(false) // 标记是否已完成初始化
const contextData = ref({
  packId: '',
  packMeta: '',
  centerIndex: 0, // 中心日志的索引（__index_number__ = 0 的位置）
  hasMoreBefore: true,
  hasMoreAfter: true,
  loadingDirection: null, // 'before' | 'after'
  originalPackId: '', // 原始的packId，用于计算序号
  originalPackMeta: '' // 原始的packMeta
})

// 查看完整内容对话框
const fullContentDialogVisible = ref(false)
const fullContentData = ref({
  title: '',
  content: ''
})

// 查询参数
const queryParams = ref({
  logstore: '',
  platform: '',
  region: '',
  project: '',
  from: '',
  to: '',
  lines: 100,
  offset: 0,
  query: '',
  fields: ''
})

// 计算属性
const messageField = computed(() => {
  return currentProduct.value?.fields?.message || 'message'
})

const timeField = computed(() => {
  return currentProduct.value?.fields?.['time:'] || 'time'
})

// 时间快捷选项
const timeShortcuts = [
  {
    text: '最近1小时',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setHours(start.getHours() - 1)
      return [start, end]
    }
  },
  {
    text: '最近3小时',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setHours(start.getHours() - 3)
      return [start, end]
    }
  },
  {
    text: '最近6小时',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setHours(start.getHours() - 6)
      return [start, end]
    }
  },
  {
    text: '最近12小时',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setHours(start.getHours() - 12)
      return [start, end]
    }
  },
  {
    text: '最近1天',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 1)
      return [start, end]
    }
  },
  {
    text: '最近3天',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 3)
      return [start, end]
    }
  },
  {
    text: '最近7天',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 7)
      return [start, end]
    }
  }
]

// 方法
const loadProductList = async () => {
  try {
    const data = await http.getSlsLogProductList()
    if (data && Array.isArray(data)) {
      productList.value = data
    }
  } catch (error) {
    console.error('加载日志项目列表失败:', error)
    ElMessage.error('加载日志项目列表失败')
  }
}

const handleProductChange = async () => {
  const product = productList.value.find(p => p.name === selectedProduct.value)
  if (!product) return

  currentProduct.value = product
  queryParams.value.logstore = product.logstore
  queryParams.value.platform = product.platform
  queryParams.value.region = product.region
  queryParams.value.project = product.project

  // 加载字段列表
  await loadFields()

  // 设置默认字段
  if (product.fields) {
    const defaultFields = [product.fields['time:'], product.fields.message].filter(Boolean)
    selectedFields.value = defaultFields
    queryParams.value.fields = defaultFields.join(',')
  }

  // 清空日志
  logs.value = []
  total.value = 0

  // 自动查询最近1小时
  if (timeRange.value && timeRange.value.length === 2) {
    await handleSearch()
  }
}

const loadFields = async () => {
  if (!currentProduct.value) return

  try {
    const data = await http.getSlsLogFields({
      logstore: currentProduct.value.logstore,
      platform: currentProduct.value.platform,
      region: currentProduct.value.region,
      project: currentProduct.value.project
    })

    if (data && data.fields) {
      // 排除上下文字段
      const excludeContextFields = ['__tag__:__pack_id__', '__pack_meta__']
      let fields = data.fields.filter(field => !excludeContextFields.includes(field))
      
      // 排序：不含下划线的在前，含下划线的在后
      fields.sort((a, b) => {
        const aHasUnderscore = a.includes('_')
        const bHasUnderscore = b.includes('_')
        
        if (aHasUnderscore === bHasUnderscore) {
          return a.localeCompare(b)
        }
        return aHasUnderscore ? 1 : -1
      })
      
      availableFields.value = fields
    }
  } catch (error) {
    console.error('加载字段列表失败:', error)
  }
}

const handleTimeChange = () => {
  if (timeRange.value && timeRange.value.length === 2) {
    queryParams.value.from = timeRange.value[0]
    queryParams.value.to = timeRange.value[1]
  }
}

const handleFieldsChange = async () => {
  // 至少保留一个字段
  if (selectedFields.value.length === 0) {
    ElMessage.warning('至少需要选择一个显示字段')
    // 恢复默认字段
    if (currentProduct.value?.fields) {
      const defaultFields = [currentProduct.value.fields['time:'], currentProduct.value.fields.message].filter(Boolean)
      selectedFields.value = defaultFields
    }
    return
  }
  
  queryParams.value.fields = selectedFields.value.join(',')
  
  // 如果已经有查询结果，自动重新查询
  if (logs.value.length > 0 && selectedProduct.value && timeRange.value && timeRange.value.length === 2) {
    await handleSearch()
  }
}

const handleSearch = async () => {
  if (!selectedProduct.value) {
    ElMessage.warning('请先选择日志项目')
    return
  }

  if (!timeRange.value || timeRange.value.length !== 2) {
    ElMessage.warning('请选择时间范围')
    return
  }

  // 重置分页
  queryParams.value.offset = 0
  logs.value = []
  total.value = 0
  hasNext.value = false

  await queryLogs()
}

const handleRealtimeQuery = async () => {
  if (!selectedProduct.value) {
    ElMessage.warning('请先选择日志项目')
    return
  }

  // 设置最近1分钟的时间范围（东八区）
  const end = new Date()
  const start = new Date(end.getTime() - 60 * 1000) // 1分钟前
  
  // 格式化为本地时间字符串
  const formatLocalTime = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  }
  
  timeRange.value = [
    formatLocalTime(start),
    formatLocalTime(end)
  ]
  handleTimeChange()

  // 执行查询
  await handleSearch()
}

const queryLogs = async () => {
  if (loading.value || loadingMore.value) return

  if (queryParams.value.offset === 0) {
    loading.value = true
  } else {
    loadingMore.value = true
  }

  try {
    const data = await http.querySlsLog(queryParams.value)

    if (data) {
      if (queryParams.value.offset === 0) {
        logs.value = data.data || []
      } else {
        logs.value.push(...(data.data || []))
      }

      total.value = data.total || 0
      hasNext.value = data.hasNext || false
      queryMode.value = data.queryMode || 'simple'

      // 更新offset
      queryParams.value.offset = logs.value.length
    }
  } catch (error) {
    console.error('查询日志失败:', error)
    ElMessage.error('查询日志失败')
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

const handleScroll = async () => {
  const container = logContainer.value
  if (!container) return

  // 检查是否滚动到底部
  const scrollTop = container.scrollTop
  const scrollHeight = container.scrollHeight
  const clientHeight = container.clientHeight

  // 距离底部100px时加载更多
  if (scrollHeight - scrollTop - clientHeight < 100) {
    if (hasNext.value && !loadingMore.value && !loading.value) {
      await queryLogs()
    }
  }

  // 检查是否需要吸顶
  isSticky.value = scrollTop > 0
}

const handleReset = () => {
  queryParams.value.query = ''
  timeRange.value = []
  queryParams.value.from = ''
  queryParams.value.to = ''
}

const handleClear = () => {
  logs.value = []
  total.value = 0
  hasNext.value = false
  queryParams.value.offset = 0
}

const getDisplayFields = (log) => {
  // 获取需要显示的额外字段（除了时间、消息和上下文字段）
  const excludeFields = [
    messageField.value, 
    timeField.value, 
    '__time__', 
    'message', 
    'time',
    '__tag__:__pack_id__',
    '__pack_meta__'
  ]
  const allFields = Object.keys(log)
  
  // 如果用户选择了字段，只显示选中的字段（排除时间和消息）
  if (selectedFields.value.length > 0) {
    return selectedFields.value.filter(field => 
      !excludeFields.includes(field) && allFields.includes(field)
    )
  }
  
  // 否则显示所有额外字段
  return allFields.filter(key => !excludeFields.includes(key))
}

const formatTime = (timeValue) => {
  if (!timeValue) return '-'
  
  // 如果已经是格式化的时间字符串，直接返回
  if (typeof timeValue === 'string' && timeValue.includes('-')) {
    return timeValue
  }
  
  // 处理时间戳
  let timestamp = parseInt(timeValue)
  if (isNaN(timestamp)) return timeValue
  
  // 10位时间戳转13位
  if (timestamp.toString().length === 10) {
    timestamp = timestamp * 1000
  }
  
  // 转换为日期
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return timeValue
  
  // 格式化为 YYYY-MM-DD HH:mm:ss
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

const formatFieldValue = (value) => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const selectLine = (index) => {
  // 可以添加行选中效果或复制功能
  console.log('选中行:', index + 1)
}

const showFullContent = (title, content) => {
  fullContentData.value = {
    title,
    content: String(content)
  }
  fullContentDialogVisible.value = true
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    ElMessage.success('已复制到剪贴板')
  } catch (error) {
    console.error('复制失败:', error)
    ElMessage.error('复制失败')
  }
}

const showContext = async (log) => {
  const packId = log['__tag__:__pack_id__']
  const packMeta = log['__pack_meta__']
  
  if (!packId || !packMeta) {
    ElMessage.warning('该日志缺少上下文信息')
    return
  }

  // 重置上下文数据
  contextData.value = {
    packId,
    packMeta,
    centerIndex: -1,
    hasMoreBefore: true,
    hasMoreAfter: true,
    loadingDirection: null,
    originalPackId: packId,
    originalPackMeta: packMeta
  }
  contextLogs.value = []
  contextInitialized.value = false
  
  // 打开对话框
  contextDialogVisible.value = true
  
  // 加载初始上下文（前后30条）
  await loadInitialContext()
}

const loadInitialContext = async () => {
  contextLoading.value = true
  
  try {
    const data = await http.contextQueryLogs({
      logstore: queryParams.value.logstore,
      platform: queryParams.value.platform,
      region: queryParams.value.region,
      project: queryParams.value.project,
      packId: contextData.value.packId,
      packMeta: contextData.value.packMeta,
      backLines: 30,
      forwardLines: 30
    })

    if (data && data.data) {
      // 找到中心日志的索引（__index_number__ = '0'）
      const centerIndex = data.data.findIndex(log => log.__index_number__ === '0')
      
      if (centerIndex >= 0) {
        contextLogs.value = data.data
        contextData.value.centerIndex = centerIndex
        
        contextData.value.hasMoreBefore = true
        contextData.value.hasMoreAfter = true
        
        // 等待DOM更新后滚动到中心日志
        await nextTick()
        await nextTick() // 多等一帧确保渲染完成
        scrollToCurrentLog()
        
        // 延迟标记初始化完成，避免滚动触发加载
        setTimeout(() => {
          contextInitialized.value = true
        }, 500)
      }
    }
  } catch (error) {
    console.error('加载上下文失败:', error)
    ElMessage.error('加载上下文失败')
  } finally {
    contextLoading.value = false
  }
}

const scrollToCurrentLog = () => {
  if (currentLogLine.value && currentLogLine.value.length > 0) {
    const element = currentLogLine.value[0]
    // 使用 auto 而不是 smooth，避免触发滚动事件
    element.scrollIntoView({ behavior: 'auto', block: 'center' })
  }
}

const handleContextScroll = async () => {
  const container = contextLogContainer.value
  if (!container || contextLoading.value || !contextInitialized.value) return

  const scrollTop = container.scrollTop
  const scrollHeight = container.scrollHeight
  const clientHeight = container.clientHeight

  // 滚动到顶部，加载更早的日志
  if (scrollTop < 100 && contextData.value.hasMoreBefore) {
    await loadMoreContext(true)
  }

  // 滚动到底部，加载更晚的日志
  if (scrollHeight - scrollTop - clientHeight < 100 && contextData.value.hasMoreAfter) {
    await loadMoreContext(false)
  }
}

const loadMoreContext = async (loadBefore) => {
  if (contextLoading.value) return
  
  contextLoading.value = true
  contextData.value.loadingDirection = loadBefore ? 'before' : 'after'
  
  try {
    let targetLog
    if (loadBefore) {
      // 向上加载：取第一条日志
      targetLog = contextLogs.value[0]
    } else {
      // 向下加载：取最后一条日志
      targetLog = contextLogs.value[contextLogs.value.length - 1]
    }
    
    if (!targetLog) return
    
    const data = await http.contextQueryLogs({
      logstore: queryParams.value.logstore,
      platform: queryParams.value.platform,
      region: queryParams.value.region,
      project: queryParams.value.project,
      packId: targetLog['__tag__:__pack_id__'],
      packMeta: targetLog['__pack_meta__'],
      backLines: loadBefore ? 30 : 0,
      forwardLines: loadBefore ? 0 : 30
    })

    if (data && data.data && data.data.length > 0) {
      const oldScrollHeight = contextLogContainer.value?.scrollHeight || 0
      
      // 过滤掉重复的日志
      let newLogs = data.data
      if (loadBefore) {
        // 向上加载：移除返回结果的最后一条（与当前第一条重复）
        if (newLogs.length > 1) {
          const lastLog = newLogs[newLogs.length - 1]
          const firstCurrentLog = contextLogs.value[0]
          // 比较 packId 和 packMeta 来判断是否重复
          if (lastLog['__tag__:__pack_id__'] === firstCurrentLog['__tag__:__pack_id__'] &&
              lastLog['__pack_meta__'] === firstCurrentLog['__pack_meta__']) {
            newLogs = newLogs.slice(0, -1)
          }
        }
        
        if (newLogs.length > 0) {
          // 插入到数组开头
          contextLogs.value = [...newLogs, ...contextLogs.value]
          contextData.value.centerIndex += newLogs.length
          
          // 保持滚动位置
          await nextTick()
          if (contextLogContainer.value) {
            const newScrollHeight = contextLogContainer.value.scrollHeight
            contextLogContainer.value.scrollTop += (newScrollHeight - oldScrollHeight)
          }
        } else {
          contextData.value.hasMoreBefore = false
        }
      } else {
        // 向下加载：移除返回结果的第一条（与当前最后一条重复）
        if (newLogs.length > 1) {
          const firstLog = newLogs[0]
          const lastCurrentLog = contextLogs.value[contextLogs.value.length - 1]
          // 比较 packId 和 packMeta 来判断是否重复
          if (firstLog['__tag__:__pack_id__'] === lastCurrentLog['__tag__:__pack_id__'] &&
              firstLog['__pack_meta__'] === lastCurrentLog['__pack_meta__']) {
            newLogs = newLogs.slice(1)
          }
        }
        
        if (newLogs.length > 0) {
          // 追加到数组末尾
          contextLogs.value = [...contextLogs.value, ...newLogs]
        } else {
          contextData.value.hasMoreAfter = false
        }
      }
      
      // 检查是否还有更多数据
      if (newLogs.length === 0 || newLogs.length < 30) {
        if (loadBefore) {
          contextData.value.hasMoreBefore = false
        } else {
          contextData.value.hasMoreAfter = false
        }
      }
    } else {
      // 没有更多数据
      if (loadBefore) {
        contextData.value.hasMoreBefore = false
      } else {
        contextData.value.hasMoreAfter = false
      }
    }
  } catch (error) {
    console.error('加载更多上下文失败:', error)
    ElMessage.error('加载更多上下文失败')
  } finally {
    contextLoading.value = false
    contextData.value.loadingDirection = null
  }
}

// 计算日志的序号（基于中心日志）
const getLogLineNumber = (index) => {
  if (contextData.value.centerIndex < 0) return index + 1
  return index - contextData.value.centerIndex
}

// 生命周期
onMounted(async () => {
  await loadProductList()

  // 设置默认时间范围（最近1小时）
  const end = new Date()
  const start = new Date()
  start.setHours(start.getHours() - 1)
  timeRange.value = [
    start.toISOString().slice(0, 19).replace('T', ' '),
    end.toISOString().slice(0, 19).replace('T', ' ')
  ]
  handleTimeChange()
})

onUnmounted(() => {
  // 清理
})
</script>

<style scoped>
.sls-log-query {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.query-header {
  flex-shrink: 0;
  padding: 16px;
  background: #fff;
  transition: all 0.3s;
}

.query-header.is-sticky {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.query-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.help-icon {
  cursor: help;
  color: #909399;
  font-size: 16px;
}

.help-icon:hover {
  color: #409eff;
}

.query-help {
  padding: 8px;
  max-width: 500px;
}

.help-title {
  font-weight: bold;
  margin-bottom: 8px;
  color: #303133;
}

.help-item {
  margin: 4px 0;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}

.form-actions {
  display: flex;
  gap: 8px;
}

.stats-bar {
  flex-shrink: 0;
  display: flex;
  gap: 24px;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  font-size: 14px;
}

.stats-item {
  display: flex;
  align-items: center;
}

.stats-label {
  color: #909399;
  margin-right: 4px;
}

.stats-value {
  color: #409eff;
  font-weight: 600;
}

.log-content {
  flex: 1;
  overflow: auto;
  padding: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin: 0 16px 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  width: 100%;
  align-self: center;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  gap: 16px;
  color: #909399;
  font-size: 14px;
  width: 100%;
  align-self: center;
}

.log-list {
  display: flex;
  flex-direction: column;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  min-width: max-content;
  width: 100%;
  align-items: flex-start;
}

.log-line {
  display: block;
  padding: 4px 12px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.1s;
  white-space: nowrap;
  min-height: 32px;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.log-line:hover {
  background-color: #e8f4ff;
}

.log-line-odd {
  background-color: #fafafa;
}

.log-line-odd:hover {
  background-color: #e8f4ff;
}

.log-actions {
  flex-shrink: 0;
  width: 60px;
  display: inline-block;
  font-size: 12px;
  text-align: left;
  margin-right: 12px;
  vertical-align: top;
}

.log-line-number {
  color: #999;
  flex-shrink: 0;
  width: 50px;
  display: inline-block;
  text-align: left;
  font-size: 12px;
  user-select: none;
  padding-right: 12px;
  margin-right: 12px;
  border-right: 1px solid #e0e0e0;
  vertical-align: top;
}

.log-time {
  color: #666;
  flex-shrink: 0;
  width: 160px;
  display: inline-block;
  font-weight: 500;
  text-align: left;
  vertical-align: top;
}

.log-separator {
  color: #ddd;
  margin: 0 8px;
  flex-shrink: 0;
  display: inline-block;
  vertical-align: top;
}

.log-message {
  color: #333;
  white-space: pre;
  flex-shrink: 0;
  display: inline-block;
  min-width: 200px;
  cursor: pointer;
  position: relative;
  text-align: left;
  vertical-align: top;
}

.log-message:hover {
  color: #409eff;
}

.log-message::after {
  content: '📋';
  position: absolute;
  right: -20px;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
  font-size: 12px;
}

.log-message:hover::after {
  opacity: 1;
}

.log-field {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
  margin-left: 8px;
  vertical-align: top;
}

.field-label {
  color: #666;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  text-align: left;
  display: inline-block;
  vertical-align: top;
}

.field-content {
  color: #0066cc;
  font-size: 12px;
  white-space: pre;
  flex-shrink: 0;
  cursor: pointer;
  position: relative;
  display: inline-block;
  text-align: left;
  vertical-align: top;
}

.field-content:hover {
  color: #66b1ff;
}

.field-content::after {
  content: '📋';
  position: absolute;
  right: -20px;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
  font-size: 12px;
}

.field-content:hover::after {
  opacity: 1;
}

.log-actions {
  margin-left: auto;
  padding-left: 12px;
  flex-shrink: 0;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #909399;
  font-size: 14px;
}

.no-more {
  padding: 20px 0;
  text-align: center;
  color: #909399;
  font-size: 14px;
}

/* 滚动条样式 */
.log-content::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.log-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.log-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.log-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式 */
@media (max-width: 1200px) {
  .form-row {
    flex-direction: column;
  }

  .form-item {
    width: 100%;
  }
}

/* 上下文对话框样式 */
.context-container {
  display: flex;
  flex-direction: column;
  height: 70vh;
}

.context-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 12px;
}

.context-info {
  font-size: 14px;
  color: #606266;
}

.context-log-list {
  flex: 1;
  overflow: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #ffffff;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.context-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  color: #909399;
  font-size: 14px;
  background: #f5f7fa;
}

.context-no-more {
  text-align: center;
  padding: 12px;
  color: #c0c4cc;
  font-size: 13px;
}

.context-log-line {
  display: block;
  padding: 4px 12px;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
  min-height: 32px;
  min-width: max-content;
  width: 100%;
  text-align: left;
}

.context-log-line-odd {
  background-color: #fafafa;
}

.context-log-line-current {
  background-color: #fff7e6;
  border-top: 2px solid #faad14;
  border-bottom: 2px solid #faad14;
}

.context-line-number {
  color: #999;
  flex-shrink: 0;
  width: 50px;
  text-align: left;
  font-size: 12px;
  user-select: none;
  padding-right: 12px;
  margin-right: 12px;
  border-right: 1px solid #e0e0e0;
  display: inline-block;
  vertical-align: top;
}

.context-line-number.current-line {
  color: #faad14;
  font-weight: bold;
}

.context-log-list .log-time {
  text-align: left;
  vertical-align: top;
}

.context-log-list .log-message {
  text-align: left;
  white-space: pre;
  flex-shrink: 0;
  vertical-align: top;
}

.context-log-list .field-label {
  text-align: left;
  vertical-align: top;
}

.context-log-list .field-content {
  text-align: left;
  white-space: pre;
  flex-shrink: 0;
  vertical-align: top;
}

/* 字段选择下拉框样式修复 */
:deep(.field-select-popper) {
  z-index: 9999 !important;
}

/* 完整内容对话框 */
.full-content-container {
  max-height: 70vh;
  overflow: auto;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
  color: #333;
}
</style>
